"use client";

import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/providers/auth-provider';
import { api } from '@/services';
import { track } from '@/lib/analytics/client';
import { useUIStore } from '@/stores/ui-store';

export type MonitorItem = { app_id: string; country_code: string; created_at: string }

function isUnauthorized(err: any) {
  return err?.status === 401 || err?.message?.includes('Unauthorized');
}

/** The dialog the whole app opens for a signed-out write; the event already has a listener. */
export function openLoginDialog() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('open-login-dialog', {
    detail: { title: 'Login to Start Monitor Apps', description: "It's completely free" },
  }));
}

export function useMonitors() {
  const { session } = useAuth();
  const qc = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  // Whole query object is spread into the hook's return (`...list`); consumers use the full query API.
  // oxlint-disable-next-line react-doctor/query-destructure-result
  const list = useQuery({
    queryKey: ['monitors'],
    queryFn: async () => {
      const result = await api.get<{ items: MonitorItem[] }>('/monitors');
      return result.items || [];
    },
    staleTime: 60_000,
    retry: 1,
    // Monitors are per account: signed out, the request could only ever 401.
    enabled: !!session,
  });

  const add = useMutation({
    mutationFn: async (input: { app_id: string | number; country_code: string }) => {
      return api.post('/monitors', {
        app_id: input.app_id,
        country_code: input.country_code.toLowerCase(),
      });
    },
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: ['monitors'] });
      const previous = qc.getQueryData<MonitorItem[]>(['monitors']) || [];
      const appId = String(input.app_id);
      const country = (input.country_code || 'us').toLowerCase();
      const exists = previous.some((it) => it.app_id === appId && (it.country_code || 'us').toLowerCase() === country);
      if (!exists) {
        const optimistic: MonitorItem = { app_id: appId, country_code: country, created_at: new Date().toISOString() };
        qc.setQueryData<MonitorItem[]>(['monitors'], [...previous, optimistic]);
      }
      return { previous } as { previous: MonitorItem[] };
    },
    onSuccess: (_data, input) => {
      addToast('App added to monitors', 'success', 3000);
      track('monitor_added', { app_id: String(input.app_id), country: input.country_code.toLowerCase() });
    },
    onError: (err: any, _input, context) => {
      if (context?.previous) qc.setQueryData(['monitors'], context.previous);
      // The caller reads `add.error` and says so on screen; there is no toast surface
      // in this app to say it for us (SPEC 32).
      if (isUnauthorized(err)) openLoginDialog();
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['monitors'] });
      qc.invalidateQueries({ queryKey: ['monitor-summary'] });
    },
  });

  const remove = useMutation({
    mutationFn: async (input: { app_id: string | number; country_code: string }) => {
      return api.request('/monitors', {
        method: 'DELETE',
        body: {
          app_id: input.app_id,
          country_code: input.country_code.toLowerCase(),
        },
      });
    },
    onMutate: async (input) => {
      // Cancel outgoing refetches
      await qc.cancelQueries({ queryKey: ['monitors'] });
      
      // Snapshot current data
      const previous = qc.getQueryData<MonitorItem[]>(['monitors']) || [];
      
      // Optimistically update cache
      const appId = String(input.app_id);
      const country = (input.country_code || 'us').toLowerCase();
      const next = previous.filter(
        (it) => !(it.app_id === appId && (it.country_code || 'us').toLowerCase() === country)
      );
      qc.setQueryData<MonitorItem[]>(['monitors'], next);
      
      return { previous };
    },
    onSuccess: (_data, input) => {
      addToast('App removed from monitors', 'success', 3000);
      track('monitor_removed', { app_id: String(input.app_id), country: input.country_code.toLowerCase() });
    },
    onError: (err: any, _input, context) => {
      // Rollback on error
      if (context?.previous) {
        qc.setQueryData(['monitors'], context.previous);
      }
      if (isUnauthorized(err)) openLoginDialog();
    },
    onSettled: () => {
      // Refetch to ensure consistency
      qc.invalidateQueries({ queryKey: ['monitors'] });
      qc.invalidateQueries({ queryKey: ['monitor-summary'] });
    },
  });

  const byKey = useMemo(() => {
    const m = new Map<string, MonitorItem>();
    (list.data || []).forEach((it) => m.set(`${it.app_id}:${(it.country_code || 'us').toLowerCase()}`, it));
    return m;
  }, [list.data]);

  return { ...list, add, remove, byKey };
}


